// ** React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// ** Next Imports
import { useNavigate } from 'react-router-dom'
// ** MUI Imports
import { Box, Button, Tooltip, Typography } from '@mui/material'

// ** Components Imports
import CustomAvatar from '../avatar'
import CustomChip from '../chip'
import IconifyIcon from '../icon'
import OverlayLoading from '../overlay-loading'

// ** Utils Imports
import {
  CHAT_BOT_ACTION_TYPES,
  CHALLENGE_TYPE_LABEL
} from '../../utils/constant'
import { downloadCandidateResume } from '../../axios/api/candidate-details'
import { downloadPDFBlob, getInitials, showErrorToast } from '../../utils/utils'

const CandidateStatusDetails = memo(({ row, isCandidateStatusAction }) => {
  // variables
  const challengeTypeLabel = useMemo(() => {
    if (!row?.challengeType) return ''
    return CHALLENGE_TYPE_LABEL[row.challengeType] || row.challengeType
  }, [row?.challengeType])

  const stageTooltipTitle = useMemo(() => {
    return `Stage Type: ${challengeTypeLabel} - Stage`
  }, [challengeTypeLabel])

  if (!isCandidateStatusAction) return null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mt: 1.5,
        width: '100%',
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'rgba(var(--customColors-mainRgb, 0 0 0), 0.02)',
        border: '1px solid rgba(var(--customColors-mainRgb, 0 0 0), 0.08)'
      }}
    >
      {(row?.stageName || row?.challengeType) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75
            }}
          >
            <Typography fontSize="12px" fontWeight="600" color="text.secondary">
              Stage:
            </Typography>
            <Typography fontSize="12px" fontWeight="600" color="text.primary">
              {row?.stageName || '-'}
            </Typography>
            {row?.challengeType && (
              <Tooltip title={stageTooltipTitle} arrow placement="top">
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <IconifyIcon icon="mdi:information-outline" fontSize="12px" />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {row?.status && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Typography fontSize="12px" fontWeight="600" color="text.secondary">
            Status:
          </Typography>
          <CustomChip
            rounded
            size="small"
            label={row?.status}
            sx={{ textTransform: 'capitalize', fontSize: '11px' }}
          />
        </Box>
      )}

      {(row?.score != null || row?.aiScore != null) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            pt: 1.5,
            mt: 1.5,
            borderTop:
              '1px dashed rgba(var(--customColors-mainRgb, 0 0 0), 0.1)'
          }}
        >
          {row?.score != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography fontSize="11px" color="text.secondary">
                Score:
              </Typography>
              <Typography
                fontSize="12px"
                fontWeight="700"
                color="var(--primary-main)"
              >
                {row?.score}
                {row?.outOfScore != null ? ` / ${row?.outOfScore}` : ''}
              </Typography>
            </Box>
          )}
          {row?.aiScore != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography fontSize="11px" color="text.secondary">
                AI Score:
              </Typography>
              <Typography
                fontSize="12px"
                fontWeight="700"
                color="var(--success-main, #10b981)"
              >
                {row?.aiScore}%
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
})

const CandidateListMessage = ({
  contestId,
  candidates,
  actionType,
  navigateToProfile,
  isLastMessage = false,
  onSelectCandidateClick,
  handleReEnableClick,
  handleInterviewClick
}) => {
  // Hooks
  const navigate = useNavigate()

  // State
  const [loading, setLoading] = useState(false)

  // Variable
  const isCandidateSearchAction = useMemo(
    () => actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SEARCH,
    [actionType]
  )

  const isResumeDownloadAction = useMemo(
    () => actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_RESUME,
    [actionType]
  )

  const isReenableAction = useMemo(
    () =>
      actionType === CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT &&
      Boolean(contestId),
    [actionType, contestId]
  )

  const isCandidateInterviewAction = useMemo(
    () =>
      [
        CHAT_BOT_ACTION_TYPES.SCHEDULE_INTERVIEW,
        CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW
      ].includes(actionType),
    [actionType]
  )

  const isCandidateReScheduleInterviewAction = useMemo(
    () => actionType === CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW,
    [actionType]
  )

  const isCandidateStatusAction = useMemo(() => {
    return actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_STATUS
  }, [actionType])

  const isSelectCandidateAction = useMemo(
    () =>
      actionType === CHAT_BOT_ACTION_TYPES.REJECT_CANDIDATE ||
      actionType === CHAT_BOT_ACTION_TYPES.MOVE_TO_NEXT_STAGE ||
      actionType === CHAT_BOT_ACTION_TYPES.RESEND_INVITE ||
      actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY ||
      actionType === CHAT_BOT_ACTION_TYPES.UPDATE_MEDIA_CONFIG ||
      (actionType === CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT && !contestId) ||
      actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_NAVIGATE,
    [actionType, contestId]
  )

  // Handlers
  const handleDownloadResume = useCallback(
    async (contestId, fileName, candidateId) => {
      setLoading(true)
      const response = await downloadCandidateResume({
        contestId,
        candidateId
      })
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        downloadPDFBlob(response, fileName)
      }
    },
    []
  )

  // Effects
  useEffect(() => {
    if (
      candidates?.length === 1 &&
      isLastMessage &&
      isCandidateSearchAction &&
      navigateToProfile
    ) {
      navigate(`/candidate-details/edit/${candidates?.[0]?.id}?active=general`)
    }
  }, [
    isLastMessage,
    isCandidateSearchAction,
    candidates,
    navigate,
    navigateToProfile
  ])

  return (
    <Box className="chatbot-message__markdown">
      {loading && <OverlayLoading />}
      <Box
        sx={{
          borderRadius: 2,
          overflowX: 'auto'
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'var(--primary-main)',
            fontWeight: 700,
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <IconifyIcon icon="mdi:account-group-outline" fontSize="18px" />
          {isSelectCandidateAction ? 'Select Candidate' : 'Candidates List'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {candidates?.length > 0 ? (
            candidates.map((row, index) => (
              <Box
                key={row?.id || index}
                sx={{
                  display: 'flex',
                  flexDirection: isCandidateStatusAction ? 'column' : 'row',
                  alignItems: isCandidateStatusAction ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  border: '1px solid var(--grey-200)',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <CustomAvatar
                    sx={{
                      width: 38,
                      height: 38,
                      fontWeight: 500,
                      fontSize: (theme) => theme.typography.body1.fontSize
                    }}
                  >
                    {getInitials(row?.name || '')}
                  </CustomAvatar>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        mb: 0.25
                      }}
                      noWrap
                      title={row?.name || '-'}
                    >
                      {row?.name || '-'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', lineHeight: 1 }}
                      noWrap
                      title={row?.email || '-'}
                    >
                      {row?.email || '-'}
                    </Typography>
                  </Box>
                </Box>

                <CandidateStatusDetails
                  row={row}
                  isCandidateStatusAction={isCandidateStatusAction}
                />

                {/* {row?.phoneNumber && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                      bgcolor: 'var(--grey-50)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      display: isSelectCandidateAction
                        ? { xs: 'none', sm: 'flex' }
                        : 'flex'
                    }}
                    title={row?.phoneNumber || '-'}
                  >
                    <IconifyIcon icon="mdi:phone-outline" fontSize="14px" />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
                    >
                      {row.phoneNumber}
                    </Typography>
                  </Box>
                )} */}

                {isSelectCandidateAction ? (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 2
                    }}
                    onClick={() => {
                      if (onSelectCandidateClick) {
                        onSelectCandidateClick(row)
                      }
                    }}
                  >
                    Select
                  </Button>
                ) : isResumeDownloadAction ? (
                  <IconifyIcon
                    title="Download Resume"
                    icon="material-symbols:download"
                    fontSize="18px"
                    hoverEffect
                    hoverBoxStyle={{
                      visibility: row?.resumeContestId ? 'visible' : 'hidden'
                    }}
                    onClick={() => {
                      if (row?.resumeContestId) {
                        handleDownloadResume(
                          row?.resumeContestId,
                          row?.resumeFileName,
                          row?.id
                        )
                      }
                    }}
                  />
                ) : isReenableAction ? (
                  <IconifyIcon
                    title="Re-enable Attempt"
                    icon="ic:baseline-restart-alt"
                    fontSize="18px"
                    hoverEffect
                    onClick={() => handleReEnableClick({ ...row, contestId })}
                  />
                ) : isCandidateInterviewAction ? (
                  <IconifyIcon
                    title={
                      isCandidateReScheduleInterviewAction
                        ? 'Re Schedule Interview'
                        : 'Schedule Interview'
                    }
                    icon="akar-icons:schedule"
                    fontSize="18px"
                    hoverEffect
                    onClick={() => handleInterviewClick(row)}
                  />
                ) : isCandidateStatusAction ? (
                  <></> // no action button for status
                ) : (
                  <IconifyIcon
                    title="View Profile"
                    icon="mdi:eye-outline"
                    fontSize="18px"
                    hoverEffect
                    onClick={() => {
                      navigate(
                        `/candidate-details/edit/${row?.id}?active=general`
                      )
                    }}
                  />
                )}
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              No candidates found
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default CandidateListMessage
